import { Check, CheckCircle, Ellipsis, MailCheck, MailOpen, MailWarning, Pin, PinOff } from 'lucide-react';
import { LONG_PRESS_CLASS, SwipeableRow, useLongPress } from '@/components/mobile';
import { cn } from '@/lib/utils';

/**
 * One inbox row on a phone: tap opens the conversation (or toggles it in
 * Select mode), a long press opens its action sheet, and swipes give the
 * quick actions iOS Mail has. The same actions are in the action sheet, the
 * thread's "…" menu and the Select toolbar, so nothing depends on a gesture.
 */
export function MobileConversationRow({
	selectMode,
	selected,
	disabled,
	isRead,
	isOpen,
	isPinned,
	onOpen,
	onToggleSelect,
	onShowActions,
	onToggleRead,
	onToggleOpen,
	onTogglePin,
	children,
}) {
	const longPress = useLongPress(() => onShowActions(), { disabled: selectMode || disabled });

	const activate = () => {
		if (disabled) return;
		if (selectMode) onToggleSelect();
		else onOpen();
	};

	return (
		<SwipeableRow
			fullSwipe
			disabled={selectMode || disabled}
			leadingActions={[
				{
					id: 'read',
					label: isRead ? 'Unread' : 'Read',
					icon: isRead ? MailWarning : MailCheck,
					className: 'bg-blue-500 text-white',
					onAction: onToggleRead,
				},
			]}
			trailingActions={[
				{
					id: 'more',
					label: 'More',
					icon: Ellipsis,
					className: 'bg-zinc-400 text-white dark:bg-zinc-600',
					onAction: onShowActions,
				},
				{
					id: 'pin',
					label: isPinned ? 'Unpin' : 'Pin',
					icon: isPinned ? PinOff : Pin,
					className: 'bg-amber-500 text-white',
					onAction: onTogglePin,
				},
				{
					id: 'open',
					label: isOpen ? 'Close' : 'Reopen',
					icon: isOpen ? CheckCircle : MailOpen,
					className: 'bg-indigo-500 text-white',
					onAction: onToggleOpen,
				},
			]}
		>
			{/* biome-ignore lint/a11y/useSemanticElements: the row holds badges and a select mark, so it stays a div with a role. */}
			<div
				role={selectMode ? 'checkbox' : 'button'}
				aria-checked={selectMode ? selected : undefined}
				aria-disabled={disabled || undefined}
				tabIndex={disabled ? -1 : 0}
				onClick={activate}
				onKeyDown={(event) => {
					if (event.target !== event.currentTarget) return;
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						activate();
					}
				}}
				{...longPress}
				className={cn(
					'flex min-h-[72px] w-full items-stretch bg-background pl-4 text-left outline-none transition-colors',
					'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
					'active:bg-stone-100 dark:active:bg-stone-800/70',
					LONG_PRESS_CLASS,
					selectMode && selected && 'bg-primary/[0.06] dark:bg-primary/10',
					disabled && 'opacity-95',
				)}
			>
				{selectMode && (
					<span className="flex shrink-0 items-center pr-3" aria-hidden="true">
						<span
							className={cn(
								'flex size-6 items-center justify-center rounded-full border-2 transition-colors',
								selected
									? 'border-primary bg-primary text-primary-foreground'
									: 'border-muted-foreground/40',
							)}
						>
							{selected && <Check className="size-3.5" strokeWidth={3} />}
						</span>
					</span>
				)}
				{children}
			</div>
		</SwipeableRow>
	);
}
